// src/pwa/installPrompt.ts
export type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

let deferredPrompt: BeforeInstallPromptEvent | null = null;
let initialized = false;

const subscribers = new Set<(available: boolean) => void>();

function notify() {
  const available = !!deferredPrompt;
  subscribers.forEach((fn) => fn(available));
}

export function initPwaInstallCapture() {
  if (initialized) return;
  initialized = true;

  window.addEventListener("beforeinstallprompt", (e: Event) => {
    e.preventDefault();
    deferredPrompt = e as BeforeInstallPromptEvent;
    notify();
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    notify();
  });
}

export function subscribeInstallAvailability(fn: (available: boolean) => void) {
  subscribers.add(fn);
  fn(!!deferredPrompt);

  return () => {
    subscribers.delete(fn);
  };
}

export async function promptPwaInstall(): Promise<
  | { outcome: "accepted" | "dismissed"; platform: string }
  | { outcome: "unavailable" }
> {
  if (!deferredPrompt) return { outcome: "unavailable" };

  await deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;

  // One-time use; clear after prompting
  deferredPrompt = null;
  notify();

  return choice;
}
